import moment from 'moment';
import { Fragment, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature, useLocalizedCalendarDay } from '@treams/treams-hooks';
import { RepeatIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	FlexGap,
	Heading3,
	InlineIconWrapper,
	Label,
	Paragraph,
	Pill,
	PlainCard,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { useModalLink } from '../navigation/ModalNavigation';
import microsoftTeamsIcon from './assets/microsoft-teams-icon.svg?url';
import outlookIcon from './assets/outlook-icon.svg?url';

function noteTitle(
	note: graphql.Note,
	hasOneOnOnes: boolean,
	t: ReturnType<typeof useTranslation>['t'],
) {
	if (note.title) {
		return note.title;
	}

	if (hasOneOnOnes) {
		return note.subject
			? t('oneOnOne.index.card.titleWithSubject', {
					subject: note.subject.firstName,
				})
			: t('note.index.card.title');
	}

	return t('note.index.card.title', {
		date: moment(note.createdAt).format('LL'),
	});
}

function SourcePills(props: { source?: string | null }) {
	const { t } = useTranslation();

	if (props.source === 'OUTLOOK') {
		return (
			<Pill small>
				<FlexGap alignItems="center" gap={6} px={1}>
					<img src={outlookIcon} alt="Outlook" style={{ width: 14, height: 14 }} />
					{t('common.email')}
				</FlexGap>
			</Pill>
		);
	}

	if (props.source === 'MSTEAMS') {
		return (
			<Pill small>
				<FlexGap alignItems="center" gap={6} px={1}>
					<img src={microsoftTeamsIcon} alt="Microsoft Teams" style={{ width: 14, height: 14 }} />
					{t('microsoft.teams.app.name')}
				</FlexGap>
			</Pill>
		);
	}

	return null;
}

export default Skeleton(
	function NoteCard(props: {
		note: graphql.Note;
		userID: string;
		linkPrefix?: string;
		compact?: boolean;
		modal?: boolean;
	}) {
		const { t } = useTranslation();
		const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);

		const link = useModalLink(
			`${props.linkPrefix ?? ''}/notes/${props.note.id}`,
			props.modal ?? false,
		);

		const calendarDayDate = useLocalizedCalendarDay(props.note.meeting?.startsAt, {
			includeTime: true,
		});

		const title = noteTitle(props.note, hasOneOnOnes, t);

		const cardMetadata = useMemo(() => {
			const components: ReactNode[] = [];

			if (hasOneOnOnes) {
				if (calendarDayDate) {
					components.push(
						<>
							{t('oneOnOne.index.card.date', {
								date: calendarDayDate,
							})}
							&nbsp;
							{props.note.meeting?.series && (
								<InlineIconWrapper size={16} translateY={-2}>
									<RepeatIcon />
								</InlineIconWrapper>
							)}
						</>,
					);
				}
			} else {
				components.push(
					t('note.index.card.createdAt', {
						date: moment(props.note.createdAt).format('LL'),
					}),
				);
			}
			if (!props.compact) {
				if (hasOneOnOnes) {
					components.push(
						t('oneOnOne.index.card.updated', {
							date: moment(props.note.updatedAt).fromNow(),
						}),
					);
				} else {
					components.push(
						t('note.index.card.updatedAt', {
							date: moment(props.note.updatedAt).format('LL'),
						}),
					);
				}
			}

			return components.map((component, index) => (
				<Fragment key={index}>
					{component}
					{index < components.length - 1 && <>&nbsp;·&nbsp;</>}
				</Fragment>
			));
		}, [
			hasOneOnOnes,
			props.compact,
			calendarDayDate,
			props.note.createdAt,
			props.note.updatedAt,
			props.note.meeting?.series,
			t,
		]);

		if (props.compact) {
			return (
				<PlainCard to={link}>
					<Flex alignItems="center" style={{ gap: tokens.gap.element }}>
						<Box flexGrow={1} flexShrink={1} minWidth={0}>
							<Label>{title}</Label>
						</Box>
						<SourcePills source={props.note.metadata?.source} />
						<Avatar.Stack small users={props.note.collaborators.edges.map((edge) => edge.node)} />
					</Flex>
				</PlainCard>
			);
		}

		return (
			<PlainCard to={link}>
				<Flex alignItems="center" flexDirection="row">
					<Box flexGrow={1} flexShrink={1}>
						<Heading3>{title}</Heading3>
						<Paragraph mt="4px">{cardMetadata}</Paragraph>
					</Box>
					<FlexGap gap={tokens.gap.element} flexGrow={0} flexShrink={0} alignItems="center">
						<SourcePills source={props.note.metadata?.source} />
						{hasOneOnOnes ? (
							<Avatar.Stack users={props.note.collaborators.edges.map((edge) => edge.node)} />
						) : (
							<Avatar.List nodes={props.note.collaborators.edges} />
						)}
					</FlexGap>
				</Flex>
			</PlainCard>
		);
	},
	function NoteCardSkeleton(props: { compact?: boolean }) {
		if (props.compact) {
			return (
				<PlainCard.Skeleton>
					<Flex alignItems="center" style={{ gap: tokens.gap.element }}>
						<Box flexGrow={1} flexShrink={1} minWidth={0}>
							<Label.Skeleton />
						</Box>
						<Avatar.Skeleton size={24} />
						<Avatar.Skeleton size={24} />
					</Flex>
				</PlainCard.Skeleton>
			);
		}

		return (
			<PlainCard.Skeleton>
				<Flex alignItems="center">
					<Box flexGrow={1} flexShrink={1}>
						<Heading3.Skeleton />
						<Paragraph.Skeleton mt="4px" lines={1} />
					</Box>
					<Flex flexGrow={0} flexShrink={0}>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
						<Box mr={2}>
							<Avatar.Skeleton size={32} />
						</Box>
					</Flex>
				</Flex>
			</PlainCard.Skeleton>
		);
	},
);
